import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { AlertTriangle, Clock, Scale, Info, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";

const limitations = [
  {
    icon: <Clock className="h-5 w-5" />,
    title: "Automatic Checks Have Limits",
    description: "The free checker runs algorithms against public data. No human is involved. It's fast and useful — but it can't catch everything.",
    implications: [
      "Results depend on what public databases know at the time",
      "Brand-new scam sites may not be in threat feeds yet",
      "Context that needs human judgment isn't considered",
      "For human-reviewed verification, see the Verified Badge Program",
    ],
  },
  {
    icon: <AlertTriangle className="h-5 w-5" />,
    title: "Website Status Can Change",
    description: "A safe website today could be compromised tomorrow. A risky site could clean up its act. Our results are a snapshot, not a permanent verdict.",
    implications: [
      "A previously safe website can become dangerous if hacked",
      "New websites build trust over time with positive signals",
      "Threat databases update at different speeds",
    ],
  },
  {
    icon: <Scale className="h-5 w-5" />,
    title: "This Isn't a Legal Judgment",
    description: "Our badges tell you what the data says. They don't make legal claims about fraud, criminal activity, or business legitimacy.",
    implications: [
      "A red badge doesn't prove illegal activity",
      "A green badge doesn't provide legal protection",
      "We report data signals — not intentions",
    ],
  },
  {
    icon: <Info className="h-5 w-5" />,
    title: "No Tool Is 100% Accurate",
    description: "Even with multiple data sources, no tool — including ours — can guarantee complete accuracy. That's why we always recommend using your own judgment too.",
    implications: [
      "Use our data alongside your own research",
      "Verify sensitive transactions through multiple channels",
      "Report suspicious websites to appropriate authorities",
    ],
  },
];

const whatWeDoRight = [
  {
    title: "Use Reputable Data Sources",
    description: "We query established, publicly maintained security databases — not proprietary black boxes.",
  },
  {
    title: "Combine Multiple Signals",
    description: "No single factor decides your result. Domain age, SSL, threat data — all weighed together.",
  },
  {
    title: "Standardized Eligibility Checks",
    description: "The Verified Badge includes a one-time check against published criteria, plus ongoing automated monitoring — a layer no database alone provides.",
  },
  {
    title: "Document Everything",
    description: "Every data source, every limitation — publicly documented. This page exists because transparency starts with us.",
  },
  {
    title: "Respect Your Privacy",
    description: "We check domains, not people. No tracking, no accounts, no data collection for the free checker.",
  },
  {
    title: "Admit Our Limits",
    description: "We tell you what we can't do. Other tools won't. We think honesty builds more trust than marketing.",
  },
];

export default function Transparency() {
  return (
    <Layout>
      <Helmet>
        <title>Transparency & Limitations — What Our Tool Can't Do | Transparency Badge</title>
        <meta name="description" content="We're honest about what our website safety checker can and can't do. Read about our limitations, our data sources, and why transparency matters." />
        <link rel="canonical" href="https://transparencybadge.com/transparency" />
      </Helmet>

      {/* Hero */}
      <section className="border-b bg-gradient-to-b from-accent/50 to-background py-16 md:py-20">
        <div className="container">
          <SectionHeader
            as="h1"
            badge="Honest Assessment"
            title="What Our Tool Can and Can't Do"
            description="No website safety tool is perfect — including ours. Here's exactly what you should know about our limitations."
          />
        </div>
      </section>

      {/* Limitations */}
      <section className="py-16 md:py-20">
        <div className="container max-w-4xl">
          <h2 className="text-2xl font-bold text-foreground mb-8 text-center">
            Limitations You Should Know About
          </h2>
          
          <div className="space-y-6">
            {limitations.map((item, index) => (
              <div
                key={index}
                className="bg-card rounded-xl border p-6 animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-caution/10 text-caution">
                    {item.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-foreground mb-2">{item.title}</h3>
                    <p className="text-muted-foreground mb-4">{item.description}</p>
                    <div className="bg-muted/50 rounded-lg p-4">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                        What this means
                      </p>
                      <ul className="space-y-1">
                        {item.implications.map((implication, i) => (
                          <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                            <span className="text-caution mt-1">•</span>
                            <span>{implication}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do Right */}
      <section className="py-16 md:py-20 bg-muted/30 border-y">
        <div className="container max-w-4xl">
          <SectionHeader
            title="What We Do to Earn Your Trust"
            description="Despite our limitations, here's what we do differently from most tools."
            align="center"
          />

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mt-10">
            {whatWeDoRight.map((item, index) => (
              <div
                key={index}
                className="bg-card rounded-xl border p-5"
              >
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-4 w-4 text-safe" />
                  <h4 className="font-semibold text-foreground text-sm">{item.title}</h4>
                </div>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deep Explanation */}
      <section className="py-16 md:py-20 border-t">
        <div className="container max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
            Why We Built a Transparency Page
          </h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              Most website safety tools don't tell you their limitations. They show you a green tick or a score and leave you to assume that means the website is safe. We think that's a disservice to users — because the gap between what a tool can confirm and what users think it's confirming can lead to real harm.
            </p>
            <p>
              This page exists because we believe you should understand exactly what you're getting when you use our checker. Informed users make better decisions than users who trust a result without understanding what went into it.
            </p>
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">The Core Limitation: Automatic Systems Check Data, Not Reality</h3>
            <p>
              Our free checker queries public databases: threat feeds, domain registration records, and SSL certificate authorities. These are real, useful signals — but they're data, not reality. A website can have a clean record in every database we check and still be running a dishonest operation. A new website with no data history could be completely legitimate.
            </p>
            <p>
              This is not a flaw in our system specifically — it's a fundamental limitation of any automatic tool. Databases track history. They don't know what a website owner intends to do tomorrow. They don't read product descriptions for accuracy. They don't verify whether the business behind the website is registered, has real employees, or will fulfill orders. Those are the gaps our eligibility check is designed to close.
            </p>
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">The Lag Problem: Scam Sites Exist Before They're Reported</h3>
            <p>
              When a new phishing site goes live, it doesn't immediately appear in threat databases. Researchers need to detect it, analyze it, and submit it to the relevant feeds. That process can take anywhere from a few hours to a few days. During that window, a scam site would show as "New / Unknown" on our tool — a yellow result — rather than the red it deserves.
            </p>
            <p>
              We can't eliminate this lag. What we can do is make sure users understand it exists. If you're checking a website that you found through an unsolicited link — a forwarded message, a social media ad, an email you didn't expect — treat even a yellow result with caution, not confidence.
            </p>
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">Why the Verified Badge Program Goes Beyond Automatic Checks</h3>
            <p>
              The Verified Badge Program exists precisely because we know automatic checks have limits. When a website owner applies for verification, their application is checked against a standardized, published set of criteria — business information, terms of service, working contact details — before their monitoring subscription begins. That's a fundamentally higher standard than automatic checks alone can produce.
            </p>
            <p>
              We're not claiming that a Verified badge means a website is perfect or that its owners are saints. It means the site passed our published eligibility criteria and found no obvious red flags at the time of review. The owner also continues paying for monthly automated monitoring — creating ongoing accountability that's absent from automatic checks alone.
            </p>
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">Our Commitment to Accuracy Over Optimism</h3>
            <p>
              Some website safety tools seem designed to reassure users rather than inform them. They show green results generously because scared users don't keep using tools. We've made the deliberate choice to be more conservative — to show yellow when we're uncertain rather than defaulting to green, and to be explicit about what our results can and cannot tell you.
            </p>
            <p>
              That means some legitimate websites show as "New / Unknown" on our tool, and their owners sometimes contact us frustrated. We understand that frustration. The answer isn't to lower our accuracy standards — it's to offer a path to verified status for legitimate sites that want to demonstrate they've been reviewed by a human. The yellow result is honest, not hostile.
            </p>
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">What This Means for How You Use Our Tool</h3>
            <p>
              Use our results as one input among several, not as the final word. A green result should make you comfortable proceeding with normal caution — not throw all caution out the window. A yellow result should prompt you to do a bit more research before sharing payment details. A red result should stop you entirely until you can verify independently that the site is legitimate.
            </p>
            <p>
              The best online safety practice is layered: use our tool, check independent reviews, look for verifiable contact information, and pay attention to your instincts. Our checker handles one layer of that — the technical data layer. The rest is judgment that belongs to you.
            </p>
          </div>
        </div>
      </section>

      {/* User Responsibility */}
      <section className="py-16 md:py-20">
        <div className="container max-w-3xl">
          <div className="bg-accent rounded-2xl p-8 md:p-10 text-center">
            <h2 className="text-2xl font-bold text-foreground mb-4">
              Your Role in Staying Safe
            </h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              Our tool gives you data. But you're the one making the decision. Here are habits that keep you safe online:
            </p>
            <ul className="text-left max-w-md mx-auto space-y-2 mb-8">
              {[
                "Double-check website URLs before entering information",
                "Use strong, unique passwords for every account",
                "Turn on two-factor authentication wherever you can",
                "Be skeptical of unsolicited links — even from people you know",
                "If something feels off about a website, trust that feeling",
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap justify-center gap-3">
              <Button asChild variant="outline">
                <Link to="/privacy">Privacy Policy</Link>
              </Button>
              <Button asChild className="btn-verified">
                <Link to="/get-verified">Get Your Site Verified</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
